import React, { useState, useEffect } from 'react'
import {
    Typography,
    TextField,
    FormControlLabel,
    RadioGroup,
    Radio,
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons'
import { useThemeStyling } from 'hooks/useThemeStyling/useThemeStyling'
import { bytesToSize } from 'utils/bytesToSize'
import { SearchableSelect, FileDnD, Icon, UploadCloudArrowUpIcon } from 'components'
import { CaseBuilderIssuerFormLabel } from './InitiateDisputeStep1/InitiateDisputeStep1'
import pdfIcon from 'assets/icons/icon-pdf.svg'
import jpgIcon from 'assets/icons/icon-jpg.svg'
import pngIcon from 'assets/icons/icon-png.svg'
import { alertSnackbarContentProps } from 'components/AlertSnackbar'
import { TFilterOption } from 'components/CaseBuilderIssuer/CaseBuilderIssuer.vm'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { updatedToRGBA } from './InitiateDisputeStep1/InitiateDisputeStep1'
import { DatePickerSecondaryWrapper } from 'components/CaseBuilderIssuerFormBuilder/formTypes/RenderDateField'
// import useIsMounted from 'hooks/utils/useIsMounted'

interface IAdditionalInfoStep3Props {
    setAlertSnackbarProps: (value: alertSnackbarContentProps) => void
    setAlertSnackbarOpen: (status: boolean) => void
    productServiceList: TFilterOption[]
    typeList: TFilterOption[]
    fileTemplateList: TFilterOption[]
    selectedDisputeReasonId: number | null
    formInfo: { [key: string]: any }
    handleChangeFormSwitch: (accessor: string, input: any) => void
    handleChangeFormField: (accessor: string, input: any) => void
    setEnableSaveAndContinue: (enable: boolean) => void
    className?: string
}

export const AdditionalInfoStep3 = ({
    setAlertSnackbarProps,
    setAlertSnackbarOpen,
    productServiceList,
    typeList,
    fileTemplateList,
    selectedDisputeReasonId,
    formInfo,
    handleChangeFormSwitch,
    handleChangeFormField,
    setEnableSaveAndContinue,
    className = '',
}: IAdditionalInfoStep3Props) => {
    const { clientTheme } = useThemeStyling()

    // const { isMounted } = useIsMounted()
    const [uploadedFiles, setUploadedFiles] = useState<any[]>([])

    const stylesObj = {
        textInputField: {
            borderRadius: clientTheme.selectionBox.borderRadius,
            '& > div input': {
                padding: '7px',
            },
            '& > .MuiSelect-select': {
                padding: '7px',
            },
            '& .MuiOutlinedInput-root': {
                borderRadius: clientTheme.selectionBox.borderRadius,
            },
            '& .MuiInputBase-input': {
                color: clientTheme.typography.fontColor.primaryText,
            },
            '& .MuiSvgIcon-root': {
                fontSize: '18px',
            },
        },
        textInputMultiField: {
            borderRadius: clientTheme.selectionBox.borderRadius,
            '& .MuiOutlinedInput-root': {
                borderRadius: clientTheme.selectionBox.borderRadius,
                padding: '7px',
            },
            '& .MuiInputBase-input': {
                color: clientTheme.typography.fontColor.primaryText,
                resize: 'vertical',
            },
        },
    }

    const fileIcons: { [key: string]: any } = {
        'application/pdf': pdfIcon,
        'image/jpeg': jpgIcon,
        'image/png': pngIcon,
    }

    const handleFileDragAndDrop = (files: File[]) => {
        if (!files.length) {
            setAlertSnackbarProps({
                title: 'Error',
                message: 'Only a single PDF, JPG, or PNG file is allowed.',
                intent: 'error',
            })
            return setAlertSnackbarOpen(true)
        }
        const isDuplicateUpload = uploadedFiles.some(
            (file: File) =>
                file.name === files[0].name && file.size === files[0].size
        )
        if (isDuplicateUpload) {
            setAlertSnackbarProps({
                title: 'Error',
                message: 'A duplicate file has been uploaded.',
                intent: 'error',
            })
            return setAlertSnackbarOpen(true)
        }
        setUploadedFiles([...uploadedFiles, files[0]])
    }

    const handleUnstageFile = (selectedFileName: string) => {
        const updatedUploadFiles = uploadedFiles.filter(
            (file: File) => file.name !== selectedFileName
        )
        setUploadedFiles(updatedUploadFiles)
    }

    const handleAddTemplate = (template: any) => {
        const isDuplicateUpload = uploadedFiles.some(
            (file: File) =>
                file.name === template.name && file.size === template.file?.size
        )
        if (isDuplicateUpload) {
            setAlertSnackbarProps({
                title: 'Error',
                message: 'A duplicate file has been uploaded.',
                intent: 'error',
            })
            return setAlertSnackbarOpen(true)
        }
        const templateFile = {
            id: template.id,
            name: template.name,
            size: template.file?.size,
            type: template.file?.content_type,
        }
        setUploadedFiles([templateFile, ...uploadedFiles])
    }

    const showFraudUI = selectedDisputeReasonId
        ? [1, 2].includes(selectedDisputeReasonId)
        : undefined

    const updatedTypeList = typeList.filter((type) => {
        if (
            formInfo.typePurchased === 'Airline Service' &&
            ['PNR Locator', 'Ticket Number'].includes(type.name ?? '')
        )
            return false

        if (
            formInfo.typePurchased === 'Hotel Service' &&
            ['Folio Number', 'Booking Number'].includes(type.name ?? '')
        )
            return false
        if (
            formInfo.typePurchased === 'Car Service' &&
            ['RA Number', 'Booking Number'].includes(type.name ?? '')
        )
            return false

        return true
    })

    useEffect(() => {
        if (showFraudUI) {
            const enableBtn = formInfo.question1 && formInfo.question7
            setEnableSaveAndContinue(enableBtn)
        } else {
            const enableBtn =
                formInfo.question2 &&
                formInfo.question3 &&
                formInfo.question4 &&
                formInfo.question5 &&
                formInfo.question6
            setEnableSaveAndContinue(enableBtn)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formInfo])

    return (
        <div className={`emp-additionalInfoStep3-root ${className}`}>
            {/* left side */}
            <div className="emp-additionalInfoStep3-leftContainer">
                <div>
                    <Typography className="emp-additionalInfoStep3-header">
                        Please answer the following questions
                    </Typography>
                    <Typography className="emp-additionalInfoStep3-subHeader">
                        Please answer the following questions.
                    </Typography>
                </div>
                {showFraudUI ? (
                    <>
                        <div className="emp-additionalInfoStep3-switchContainer">
                            <CaseBuilderIssuerFormLabel
                                labelText={'Is the card in your possession?'}
                                isRequired
                                className={'emp-additionalInfoStep3-formLabel'}
                            />
                            <div className="emp-additionalInfoStep3-switch">
                                <div>
                                    <RadioGroup
                                        row
                                        value={formInfo.question1}
                                        onChange={(e) => {
                                            handleChangeFormSwitch(
                                                'question1',
                                                e.target.value
                                            )
                                        }}
                                        className="emp-additionalInfoStep3-radioGrp"
                                    >
                                        <div>
                                            <FormControlLabel
                                                value={'1'}
                                                control={
                                                    <Radio
                                                        size={'small'}
                                                        color="secondary"
                                                    />
                                                }
                                                label="Yes"
                                            />
                                        </div>
                                        <div>
                                            <FormControlLabel
                                                value={'0'}
                                                control={
                                                    <Radio
                                                        size={'small'}
                                                        color="secondary"
                                                    />
                                                }
                                                label="No"
                                            />
                                        </div>
                                    </RadioGroup>
                                </div>
                            </div>
                        </div>
                        <div className="emp-additionalInfoStep3-switchContainer">
                            <CaseBuilderIssuerFormLabel
                                labelText={
                                    'Have you attempted to contact the merchant?'
                                }
                                isRequired
                                className={'emp-additionalInfoStep3-formLabel'}
                            />
                            <div className="emp-additionalInfoStep3-switch">
                                <div>
                                    <RadioGroup
                                        row
                                        value={formInfo.question7}
                                        onChange={(e) => {
                                            handleChangeFormSwitch(
                                                'question7',
                                                e.target.value
                                            )
                                        }}
                                        className="emp-additionalInfoStep3-radioGrp"
                                    >
                                        <div>
                                            <FormControlLabel
                                                value={'1'}
                                                control={
                                                    <Radio
                                                        size={'small'}
                                                        color="secondary"
                                                    />
                                                }
                                                label="Yes"
                                            />
                                        </div>
                                        <div>
                                            <FormControlLabel
                                                value={'0'}
                                                control={
                                                    <Radio
                                                        size={'small'}
                                                        color="secondary"
                                                    />
                                                }
                                                label="No"
                                            />
                                        </div>
                                    </RadioGroup>
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="emp-additionalInfoStep3-switchContainer">
                            <CaseBuilderIssuerFormLabel
                                labelText={
                                    'Have you attempted to contact the merchant?'
                                }
                                isRequired
                                className={'emp-additionalInfoStep3-formLabel'}
                            />
                            <div className="emp-additionalInfoStep3-switch">
                                <div>
                                    <RadioGroup
                                        row
                                        value={formInfo.question2}
                                        onChange={(e) => {
                                            handleChangeFormSwitch(
                                                'question2',
                                                e.target.value
                                            )
                                        }}
                                        className="emp-additionalInfoStep3-radioGrp"
                                    >
                                        <div>
                                            <FormControlLabel
                                                value={'1'}
                                                control={
                                                    <Radio
                                                        size={'small'}
                                                        color="secondary"
                                                    />
                                                }
                                                label="Yes"
                                            />
                                        </div>
                                        <div>
                                            <FormControlLabel
                                                value={'0'}
                                                control={
                                                    <Radio
                                                        size={'small'}
                                                        color="secondary"
                                                    />
                                                }
                                                label="No"
                                            />
                                        </div>
                                    </RadioGroup>
                                </div>
                            </div>
                        </div>
                        <div>
                            <CaseBuilderIssuerFormLabel
                                labelText={
                                    'What type of product or service was purchased?'
                                }
                            />
                            <div>
                                <SearchableSelect
                                    placeholder={'Select purchase type'}
                                    value={formInfo.typePurchased ?? ''}
                                    onValueChange={(e) => {
                                        handleChangeFormField(
                                            'typePurchased',
                                            e?.name
                                        )
                                    }}
                                    options={productServiceList ?? []}
                                    accessor={'name'}
                                    testId={'typePurchased'}
                                    hideSearch
                                    selectFontSize={
                                        clientTheme.typography.body1.fontSize
                                    }
                                    isPaginate={false}
                                />
                            </div>
                        </div>
                        {formInfo.typePurchased === 'Airline Service' && (
                            <div>
                                <CaseBuilderIssuerFormLabel
                                    labelText={'PNR locator'}
                                />
                                <div>
                                    <TextField
                                        variant={'outlined'}
                                        sx={stylesObj.textInputField}
                                        required={false}
                                        type="text"
                                        name={'pnrLocator'}
                                        fullWidth
                                        placeholder="PNR Locator"
                                        value={formInfo.pnrLocator ?? ''}
                                        onChange={(e) => {
                                            handleChangeFormField(
                                                'pnrLocator',
                                                e?.target.value
                                            )
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                        {formInfo.typePurchased === 'Airline Service' && (
                            <div>
                                <CaseBuilderIssuerFormLabel
                                    labelText={'Ticket number'}
                                />
                                <div>
                                    <TextField
                                        variant={'outlined'}
                                        sx={stylesObj.textInputField}
                                        required={false}
                                        type="text"
                                        name={'ticketNumber'}
                                        fullWidth
                                        placeholder="Ticket number"
                                        value={formInfo.ticketNumber ?? ''}
                                        onChange={(e) => {
                                            handleChangeFormField(
                                                'ticketNumber',
                                                e?.target.value
                                            )
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                        {formInfo.typePurchased === 'Hotel Service' && (
                            <div>
                                <CaseBuilderIssuerFormLabel
                                    labelText={'Folio number'}
                                />
                                <div>
                                    <TextField
                                        variant={'outlined'}
                                        sx={stylesObj.textInputField}
                                        required={false}
                                        type="text"
                                        name={'folioNumber'}
                                        fullWidth
                                        placeholder="Folio number"
                                        value={formInfo.folioNumber ?? ''}
                                        onChange={(e) => {
                                            handleChangeFormField(
                                                'folioNumber',
                                                e?.target.value
                                            )
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                        {formInfo.typePurchased === 'Car Service' && (
                            <div>
                                <CaseBuilderIssuerFormLabel
                                    labelText={'RA number'}
                                />
                                <div>
                                    <TextField
                                        variant={'outlined'}
                                        sx={stylesObj.textInputField}
                                        required={false}
                                        type="text"
                                        name={'raNumber'}
                                        fullWidth
                                        placeholder="RA number"
                                        value={formInfo.raNumber ?? ''}
                                        onChange={(e) => {
                                            handleChangeFormField(
                                                'raNumber',
                                                e?.target.value
                                            )
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                        {['Hotel Service', 'Car Service'].includes(
                            formInfo.typePurchased
                        ) && (
                            <div>
                                <CaseBuilderIssuerFormLabel
                                    labelText={'Booking number'}
                                />
                                <div>
                                    <TextField
                                        variant={'outlined'}
                                        sx={stylesObj.textInputField}
                                        required={false}
                                        type="text"
                                        name={'bookingNumber'}
                                        fullWidth
                                        placeholder="Booking number"
                                        value={formInfo.bookingNumber ?? ''}
                                        onChange={(e) => {
                                            handleChangeFormField(
                                                'bookingNumber',
                                                e?.target.value
                                            )
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                        {[
                            'Hotel Service',
                            'Car Service',
                            'Airline Service',
                            'Other Service',
                        ].includes(formInfo.typePurchased) && (
                            <div>
                                <CaseBuilderIssuerFormLabel
                                    labelText={'Additional information'}
                                />
                                <div className="emp-additionalInfoStep3-additionalInfoInputContainer">
                                    <div>
                                        <SearchableSelect
                                            placeholder={'Select info type'}
                                            value={
                                                formInfo.additionalInfoType ??
                                                ''
                                            }
                                            onValueChange={(e) => {
                                                handleChangeFormField(
                                                    'additionalInfoType',
                                                    e?.name
                                                )
                                            }}
                                            options={updatedTypeList ?? []}
                                            accessor={'name'}
                                            testId={'additionalInfoType'}
                                            hideSearch
                                            selectFontSize={
                                                clientTheme.typography.body1
                                                    .fontSize
                                            }
                                            isPaginate={false}
                                        />
                                    </div>
                                    {Boolean(formInfo.additionalInfoType) && (
                                        <div>
                                            <TextField
                                                variant={'outlined'}
                                                sx={stylesObj.textInputField}
                                                required={false}
                                                type="text"
                                                name={'additionalInfoValue'}
                                                fullWidth
                                                // placeholder="Number"
                                                value={
                                                    formInfo.additionalInfoValue ??
                                                    ''
                                                }
                                                onChange={(e) => {
                                                    handleChangeFormField(
                                                        'additionalInfoValue',
                                                        e?.target.value
                                                    )
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* <div>
                            <CaseBuilderIssuerFormLabel
                                labelText={
                                    'Please describe this transaction and its purpose'
                                }
                            />
                            <div>
                                <TextField
                                    variant={'outlined'}
                                    sx={stylesObj.textInputMultiField}
                                    required={false}
                                    type="text"
                                    name={'describeItem'}
                                    fullWidth
                                    placeholder="Comments"
                                    multiline
                                    rows={3}
                                    value={formInfo.describeItem ?? ''}
                                    onChange={(e) => {
                                        handleChangeFormField(
                                            'describeItem',
                                            e?.target.value
                                        )
                                    }}
                                />
                            </div>
                        </div> */}
                        <div className="emp-additionalInfoStep3-switchContainer">
                            <CaseBuilderIssuerFormLabel
                                labelText={
                                    'Did you receive the merchandise/services as promised?'
                                }
                                isRequired
                                className={'emp-additionalInfoStep3-formLabel'}
                            />
                            <div className="emp-additionalInfoStep3-switch">
                                <div>
                                    <RadioGroup
                                        row
                                        value={formInfo.question3}
                                        onChange={(e) => {
                                            handleChangeFormSwitch(
                                                'question3',
                                                e.target.value
                                            )
                                        }}
                                        className="emp-additionalInfoStep3-radioGrp"
                                    >
                                        <div>
                                            <FormControlLabel
                                                value={'1'}
                                                control={
                                                    <Radio
                                                        size={'small'}
                                                        color="secondary"
                                                    />
                                                }
                                                label="Yes"
                                            />
                                        </div>
                                        <div>
                                            <FormControlLabel
                                                value={'0'}
                                                control={
                                                    <Radio
                                                        size={'small'}
                                                        color="secondary"
                                                    />
                                                }
                                                label="No"
                                            />
                                        </div>
                                    </RadioGroup>
                                </div>
                            </div>
                        </div>
                        {formInfo.question3 === '0' && (
                            <div className="emp-additionalInfoStep3-dateContainer">
                                <CaseBuilderIssuerFormLabel
                                    labelText={
                                        'If no, please select the date the merchandise/service was expected '
                                    }
                                />
                                <div>
                                    <DatePickerSecondaryWrapper>
                                        <LocalizationProvider
                                            dateAdapter={AdapterDayjs}
                                        >
                                            <DatePicker
                                                inputFormat="YYYY/MM/DD"
                                                value={
                                                    formInfo.fulfilledDate
                                                        ? dayjs(
                                                              formInfo.fulfilledDate
                                                          )
                                                        : null
                                                }
                                                onChange={(newValue) => {
                                                    handleChangeFormField(
                                                        'fulfilledDate',
                                                        newValue
                                                            ? newValue.format(
                                                                  'YYYY-MM-DD'
                                                              )
                                                            : ''
                                                    )
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        variant="outlined"
                                                        sx={
                                                            stylesObj.textInputField
                                                        }
                                                        required={false}
                                                        fullWidth
                                                        name="fulfilledDate"
                                                        placeholder="YYYY/MM/DD"
                                                    />
                                                )}
                                            />
                                        </LocalizationProvider>
                                    </DatePickerSecondaryWrapper>
                                </div>
                            </div>
                        )}
                        <div className="emp-additionalInfoStep3-switchContainer">
                            <CaseBuilderIssuerFormLabel
                                labelText={
                                    'Was the transaction supposed to be cancelled?'
                                }
                                isRequired
                                className={'emp-additionalInfoStep3-formLabel'}
                            />
                            <div className="emp-additionalInfoStep3-switch">
                                <div>
                                    <RadioGroup
                                        row
                                        value={formInfo.question4}
                                        onChange={(e) => {
                                            handleChangeFormSwitch(
                                                'question4',
                                                e.target.value
                                            )
                                        }}
                                        className="emp-additionalInfoStep3-radioGrp"
                                    >
                                        <div>
                                            <FormControlLabel
                                                value={'1'}
                                                control={
                                                    <Radio
                                                        size={'small'}
                                                        color="secondary"
                                                    />
                                                }
                                                label="Yes"
                                            />
                                        </div>
                                        <div>
                                            <FormControlLabel
                                                value={'0'}
                                                control={
                                                    <Radio
                                                        size={'small'}
                                                        color="secondary"
                                                    />
                                                }
                                                label="No"
                                            />
                                        </div>
                                    </RadioGroup>
                                </div>
                            </div>
                        </div>
                        <div className="emp-additionalInfoStep3-switchContainer">
                            <CaseBuilderIssuerFormLabel
                                labelText={
                                    'Was the transaction supposed to be refunded?'
                                }
                                isRequired
                                className={'emp-additionalInfoStep3-formLabel'}
                            />
                            <div className="emp-additionalInfoStep3-switch">
                                <div>
                                    <RadioGroup
                                        row
                                        value={formInfo.question5}
                                        onChange={(e) => {
                                            handleChangeFormSwitch(
                                                'question5',
                                                e.target.value
                                            )
                                        }}
                                        className="emp-additionalInfoStep3-radioGrp"
                                    >
                                        <div>
                                            <FormControlLabel
                                                value={'1'}
                                                control={
                                                    <Radio
                                                        size={'small'}
                                                        color="secondary"
                                                    />
                                                }
                                                label="Yes"
                                            />
                                        </div>
                                        <div>
                                            <FormControlLabel
                                                value={'0'}
                                                control={
                                                    <Radio
                                                        size={'small'}
                                                        color="secondary"
                                                    />
                                                }
                                                label="No"
                                            />
                                        </div>
                                    </RadioGroup>
                                </div>
                            </div>
                        </div>
                        <div className="emp-additionalInfoStep3-switchContainer">
                            <CaseBuilderIssuerFormLabel
                                labelText={
                                    'Was the transaction paid by another means?'
                                }
                                isRequired
                                className={'emp-additionalInfoStep3-formLabel'}
                            />
                            <div className="emp-additionalInfoStep3-switch">
                                <div>
                                    <RadioGroup
                                        row
                                        value={formInfo.question6}
                                        onChange={(e) => {
                                            handleChangeFormSwitch(
                                                'question6',
                                                e.target.value
                                            )
                                        }}
                                        className="emp-additionalInfoStep3-radioGrp"
                                    >
                                        <div>
                                            <FormControlLabel
                                                value={'1'}
                                                control={
                                                    <Radio
                                                        size={'small'}
                                                        color="secondary"
                                                    />
                                                }
                                                label="Yes"
                                            />
                                        </div>
                                        <div>
                                            <FormControlLabel
                                                value={'0'}
                                                control={
                                                    <Radio
                                                        size={'small'}
                                                        color="secondary"
                                                    />
                                                }
                                                label="No"
                                            />
                                        </div>
                                    </RadioGroup>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
            {/* right side */}
            <div className="emp-additionalInfoStep3-rightContainer">
                <div>
                    <Typography className="emp-additionalInfoStep3-header">
                        Supporting Documents
                    </Typography>
                    <Typography className="emp-additionalInfoStep3-subHeader">
                        Select and/or upload new documents to add to this case.
                    </Typography>
                </div>
                <div>
                    <CaseBuilderIssuerFormLabel labelText={'Select Template'} />
                    <div>
                        <SearchableSelect
                            value={formInfo.fileTemplate ?? ''}
                            onValueChange={(e) => {
                                handleChangeFormField('fileTemplate', e?.name)
                                handleAddTemplate(e)
                            }}
                            placeholder={'Select template'}
                            options={fileTemplateList ?? []}
                            accessor={'name'}
                            testId={'fileTemplate'}
                            hideSearch
                            selectFontSize={
                                clientTheme.typography.body1.fontSize
                            }
                            isPaginate={false}
                        />
                    </div>
                </div>
                <div>
                    <FileDnD
                        accepts={['.jpg', '.pdf', '.png']}
                        onDrop={handleFileDragAndDrop}
                        iconComponent={
                            <UploadCloudArrowUpIcon
                                color={clientTheme.caseBuilder.color}
                                className="emp-additionalInfoStep3-uploadIcon"
                            />
                        }
                        fileUploadInstructionText={
                            <>
                                Drag & Drop here or{' '}
                                <span style={{ textDecoration: 'underline' }}>
                                    Choose File
                                </span>
                            </>
                        }
                        fileUploadCriteria={`Supported Formats: pdf, jpg, png`}
                        enableFileDnDSlimStyling
                        hoverTheming={{
                            labelColor: clientTheme.caseBuilder.color,
                            borderColor: clientTheme.caseBuilder.color,
                            backgroundColor: updatedToRGBA(
                                clientTheme.caseBuilder.color,
                                0.05
                            ),
                        }}
                    />
                </div>
                <div className="emp-additionalInfoStep3-filesContainer">
                    {uploadedFiles.map((file: File) => {
                        return (
                            <div
                                key={`key-file-${file.name}`}
                                className="emp-additionalInfoStep3-fileContainer"
                            >
                                <div className="emp-additionalInfoStep3-fileLeftContainer">
                                    {fileIcons[file.type] && (
                                        <div>
                                            <img
                                                src={fileIcons[file.type]}
                                                alt={file.type}
                                            />
                                        </div>
                                    )}
                                    <div>
                                        <Typography style={{ fontWeight: 600 }}>
                                            {file.name}
                                        </Typography>
                                        <div className="emp-additionalInfoStep3-fileDetails">
                                            <Typography
                                                style={{
                                                    color: '#A09E9E',
                                                }}
                                            >{`${bytesToSize(
                                                file.size
                                            )}`}</Typography>
                                            <div className="emp-additionalInfoStep3-dotIcon" />
                                            <FontAwesomeIcon
                                                icon={faCheckCircle}
                                                className="emp-additionalInfoStep3-checkCircleIcon"
                                            />
                                            <Typography>
                                                Upload Complete
                                            </Typography>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <Icon
                                        className={`icon-trash-can emp-additionalInfoStep3-trashIcon`}
                                        onClick={() =>
                                            handleUnstageFile(file.name)
                                        }
                                    />
                                </div>
                            </div>
                        )
                    })}
                </div>
                <div>
                    <CaseBuilderIssuerFormLabel labelText={'Dispute Details'} />
                    <div>
                        <TextField
                            variant={'outlined'}
                            sx={stylesObj.textInputMultiField}
                            required={false}
                            type="text"
                            name={'disputeDetails'}
                            fullWidth
                            placeholder="Please provide details why you're disputing this transaction"
                            multiline
                            rows={4}
                            value={formInfo.disputeDetails ?? ''}
                            onChange={(e) => {
                                handleChangeFormField(
                                    'disputeDetails',
                                    e?.target.value
                                )
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}
